import { Application } from 'pixi.js';

import { type FC, memo, useEffect, useRef, useState } from 'react';

import { EventEmitter } from 'events';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useDebounce } from '@ipfabric/ui-components/hooks/useDebounce';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { GraphRenderer } from 'components/Graph/GraphApp/GraphRenderer';
import type { GraphAppEvents } from 'components/Graph/GraphApp/events/GraphAppEvents';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { ensureNodesInView } from 'components/Graph/commands/ensureNodesInView';
import elementResizeDetectorMaker from 'element-resize-detector';
import { Viewport } from 'pixi-viewport';
import type { TypedEventEmitter } from 'utils/events/TypedEventEmitter';
import { installEventsOnManualZoomChanges } from 'utils/pixi/installEventsOnManualZoomChanges';
import { isApple } from 'utils/platform/isApple';

interface AppComponents {
  app: Application;
  appEvents: TypedEventEmitter<GraphAppEvents>;
  viewport: Viewport;
}

const useResizeDetector = (props: {
  appComponents: AppComponents | null;
  wrapper: HTMLDivElement | null;
}) => {
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });

  const debounceEnsureNodesInView = useDebounce(100);

  useEffect(() => {
    if (props.appComponents === null) {
      return undefined;
    }
    if (props.wrapper === null) {
      stumble(
        'Unable to initialize graph resize detector, wrapper element not found',
      );
      return undefined;
    }
    const erd = elementResizeDetectorMaker({
      // recommended default for best performance according to docs
      strategy: 'scroll',
    });
    const { viewport, app } = props.appComponents;
    erd.listenTo(props.wrapper, (el) => {
      app.resize();
      viewport.resize(el.clientWidth, el.clientHeight);
      app.render();
      // debounce to avoid frequent execution on animations
      debounceEnsureNodesInView(() => {
        ctxProps.dispatch(ensureNodesInView, { strict: true });
      });
    });

    return () => {
      erd.uninstall(props.wrapper!);
    };
  }, [props.appComponents, ctxProps.dispatch, props.wrapper]);
};

/**
 * This component takes care of lifecycle of PIXI.Application and viewport only.
 * Actual graph painting and controlling code is in CanvasGraphRenderer subcomponent.
 */
const BareGraphApp: FC = () => {
  const [appComponents, setAppComponents] = useState<AppComponents | null>(
    null,
  );
  const [isSelectionMode, setSelectionMode] = useState(false);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = canvasWrapperRef.current!;
    const app = new Application({
      resizeTo: wrapper,
      backgroundColor: 0xffffff,
      // disable automatic rendering, we'll rerender only when viewport.dirty
      autoStart: false,
      autoDensity: true,
      resolution: 2,
    });

    const viewport = new Viewport({
      divWheel: wrapper,
      interaction: app.renderer.plugins.interaction,
      passiveWheel: false,
      screenWidth: wrapper.clientWidth,
      screenHeight: wrapper.clientHeight,
      stopPropagation: true,
    });
    installEventsOnManualZoomChanges(viewport);

    viewport.drag().pinch().wheel();

    wrapper.appendChild(app.view);
    app.render(); // just paint the background color
    app.stage.addChild(viewport);

    const appEvents: TypedEventEmitter<GraphAppEvents> = new EventEmitter();

    setAppComponents({
      app,
      appEvents,
      viewport,
    });

    const exitSelectionMode = () => {
      setSelectionMode(false);
      appEvents.emit('selectionModeEnd');
    };

    const domEventListeners = new ManagedDomEventListeners(
      // This prevents flickering black over the canvas when window
      // is resized, since canvas is always reset after resizing.
      // It surprisingly works fine when the canvas element is resized
      // due to layout change, however on window resize it needs
      // to immediately rerender this way.
      [
        window,
        'resize',
        () => {
          app.render();
        },
      ],
      [
        window,
        'keydown',
        (event: KeyboardEvent) => {
          if (event.shiftKey || event.metaKey || (event.ctrlKey && !isApple)) {
            setSelectionMode(true);
          }
        },
      ],
      [
        window,
        'keyup',
        (event: KeyboardEvent) => {
          if (!event.shiftKey && !event.metaKey && !event.ctrlKey) {
            exitSelectionMode();
          }
        },
      ],
      [document, 'contextmenu', exitSelectionMode],
      [window, 'blur', exitSelectionMode],
    );

    return () => {
      app.destroy(false, {
        children: true,
        // cannot cleanup textures at the moment as they are loaded once globally
        baseTexture: false,
        texture: false,
      });
      viewport.destroy();
      domEventListeners.remove();
      appEvents.emit('beforeUnmount');
      // Mitigate memory leaks: this should not be needed as appEvents should be cleaned from memory
      // on graph unmount, but event listeners may contain references to big structures and when some tool
      // (e.g. whyDidYouRender or even Chrome console) retains references to old elements,
      // old appEvents may live in memory for long time.
      appEvents.removeAllListeners();
    };
  }, []);

  useResizeDetector({
    appComponents,
    wrapper: canvasWrapperRef.current,
  });

  return (
    <div
      className={classNames(
        'canvas-graph-wrapper',
        isSelectionMode && 'cursor-copy',
      )}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      ref={canvasWrapperRef}
      tabIndex={0}
      onKeyDown={(event) => {
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.code === 'Equal' || event.code === 'NumpadAdd')
        ) {
          event.preventDefault();
          appComponents?.appEvents.emit('zoomIn');
        }
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.code === 'Minus' || event.code === 'NumpadSubtract')
        ) {
          event.preventDefault();
          appComponents?.appEvents.emit('zoomOut');
        }
      }}
    >
      {appComponents && (
        <GraphRenderer
          app={appComponents.app}
          appEvents={appComponents.appEvents}
          isSelectionMode={isSelectionMode}
          viewport={appComponents.viewport}
        />
      )}
    </div>
  );
};

BareGraphApp.whyDidYouRender = true;
export const GraphApp = memo(BareGraphApp);
